import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';

import { MAX_REPORTS_IN_SHOPPING_CART } from 'src/constants/report';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import ReportShareModal from 'src/components/common/ReportShareModal';
import FollowUpInspirationsButton from 'src/components/followUpInspirations/components/FollowUpInspirationsButton';
import { useShoppingCart } from 'src/components/report/ShoppingCartContext';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

export const REPORT_HEADER_TEST_ID = 'report-header';
export const REPORT_HEADER_COUNTER_TEST_ID = 'report-header-counter';

export interface ReportHeaderProps {
  reportOwnerId: number | string;
  reportOwnerName: string;
  showCustomReports?: boolean;
  showFollowUpInspirations?: boolean;
}

export const ReportHeader: FC<ReportHeaderProps> = ({
  reportOwnerId,
  reportOwnerName,
  showCustomReports = false,
  showFollowUpInspirations = false,
}) => {
  const { t } = useTranslation('report');
  const [showShareModal, setShowShareModal] = useState(false);
  const { resetWorkspace } = useWorkspaceContext();
  const {
    clearCart,
    downloadCustomReportBundle,
    isLoading,
    shoppingCartItems,
  } = useShoppingCart();
  const headerRef = useRef<HTMLDivElement>(null);
  const disableButtons = shoppingCartItems.length === 0 || isLoading;

  const handleCreateReportClick = () => {
    resetWorkspace();
    history.push(routePaths.WORKSPACE);
  };

  return (
    <Stack
      direction="row"
      ref={headerRef}
      data-test-id={REPORT_HEADER_TEST_ID}
      sx={{
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        py: 2,
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontWeight: 'normal',
          fontSize: 24,
        }}
      >
        {showCustomReports ? (
          <span>{t('reportDownload')}</span>
        ) : (
          <>
            <span className="report-header__reports-for">
              {t('reportFor')}:{' '}
            </span>

            <span className="report-header__reports-owner-name">
              {reportOwnerName}{' '}
            </span>

            <span className="report-header__reports-owner-id">
              {reportOwnerId}
            </span>
          </>
        )}
      </Typography>
      {showCustomReports ? (
        <>
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Typography data-test-id={REPORT_HEADER_COUNTER_TEST_ID}>
              {shoppingCartItems.length}/{MAX_REPORTS_IN_SHOPPING_CART}{' '}
              {t('reportsSelected')}
            </Typography>
          </Stack>
          <Stack
            className="report-header__tool-wrapper"
            direction="row"
            spacing={0.5}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            <Button
              disabled={disableButtons}
              onClick={() => clearCart()}
              variant="cta"
            >
              {t('common:reset')}
            </Button>

            <LoadingButton
              disabled={disableButtons}
              onClick={() => downloadCustomReportBundle()}
              loading={isLoading}
              variant="cta"
            >
              {t('downloadSelected')}
            </LoadingButton>

            <Button
              disabled={disableButtons}
              onClick={() => setShowShareModal(true)}
              variant="cta"
            >
              {t('shareSelected')}
            </Button>

            <Button onClick={() => handleCreateReportClick()} variant="cta">
              {t('reportCreate')}
            </Button>

            {showShareModal && (
              <ReportShareModal
                headline={`Share ${shoppingCartItems.length} Report(s)`}
                reportOrDataline={shoppingCartItems}
                isReport
                onClose={() => setShowShareModal(false)}
              />
            )}
          </Stack>
        </>
      ) : (
        showFollowUpInspirations && <FollowUpInspirationsButton />
      )}
    </Stack>
  );
};

export default ReportHeader;
